import AutosaveSuccess from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { useParams } from "react-router-dom-v5-compat";
import ErrorMessage from "../../../common/js/components/ErrorMessage";
import dealOperations from "../deal/ducks/operations";
import DocumentDrop from "../documents/DocumentDropContainer";
import searchOperations from "../search/ducks/operations";
import AdminToolbar from "./AdminToolbar";
import operations from "./ducks/operations";
import ExhibitAInformation from "./ExhibitAInformation";
import LicenseInformation from "./LicenseInformation";
import ProxyDrawer from "./ProxyDrawer";
import UserAccountDetail from "./UserAccountDetail";

const StyledAdminFlexContainer = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    gap: "1vw",
}));

const StyledAdminFlexItem = styled("div")(() => ({
    flex: "1 1 0",
}));

const StyledLicenseExhibitContainer = styled("div")(() => ({
    display: "flex",
    "@media (max-width: 1280px)": {
        flexDirection: "column",
        height: "auto",
    },
    height: "1300px",
    overflowY: "scroll",
}));

const AdminContainer = (props) => {
    const { errors, getUserAccount, resetUserAccountState, toggleTerminationDoc, userAccount, userProfile } = props;
    const params = useParams();
    const [openAddProxy, setOpenProxyScreen] = useState(false);
    useEffect(() => {
        const id = params.id;
        if (id) {
            getUserAccount(id);
        }
        ReactGA.send({ hitType: "pageview", page: "/admin/user/*", title: `User Account Admin: ${id}` });
        return () => {
            resetUserAccountState();
        };
    }, []);

    const toggleAddProxy = () => {
        setOpenProxyScreen(!openAddProxy);
    };

    if (includes(["dataAdmin"], get(userProfile, "role", null))) {
        return (
            <div style={{ minWidth: "800px" }}>
                <AdminToolbar {...props} toggleAddProxy={toggleAddProxy} />
                <StyledAdminFlexContainer>
                    {userAccount?.userAccountKey != null && (
                        <div style={{ padding: "8px" }}>
                            <DocumentDrop
                                entityKey={userAccount?.userAccountKey}
                                entityStatus={userAccount?.userAccountStatus}
                                type="user"
                                documentTypes="user"
                                formDisabled={false}
                                errors={errors.documents}
                                restrictPreview={false}
                                toggleTerminationDoc={toggleTerminationDoc}
                                parentType={"USER"}
                            />
                        </div>
                    )}
                    <StyledAdminFlexItem>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <AutosaveSuccess className="waterloo" style={{ paddingRight: "32px" }} /> General
                                Information
                            </AccordionSummary>
                            <Divider />
                            <AccordionDetails>
                                <UserAccountDetail {...props} />
                            </AccordionDetails>
                        </Accordion>
                    </StyledAdminFlexItem>
                    {/* All users can now add License info and docs */}
                    <StyledAdminFlexItem>
                        <StyledLicenseExhibitContainer>
                            <StyledAdminFlexItem>
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <AutosaveSuccess className="waterloo" style={{ paddingRight: "32px" }} />{" "}
                                        License Information
                                    </AccordionSummary>
                                    <Divider />
                                    <AccordionDetails>
                                        <LicenseInformation {...props} />
                                    </AccordionDetails>
                                </Accordion>
                            </StyledAdminFlexItem>
                            <StyledAdminFlexItem>
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <AutosaveSuccess className="waterloo" style={{ paddingRight: "32px" }} />{" "}
                                        Exhibit A Information
                                    </AccordionSummary>
                                    <Divider />
                                    <AccordionDetails>
                                        <ExhibitAInformation {...props} />
                                    </AccordionDetails>
                                </Accordion>
                            </StyledAdminFlexItem>
                        </StyledLicenseExhibitContainer>
                    </StyledAdminFlexItem>
                </StyledAdminFlexContainer>
                <ProxyDrawer {...props} toggleAddProxy={toggleAddProxy} open={openAddProxy} />
            </div>
        );
    }
    return <ErrorMessage />;
};

const mapStateToProps = (state) => {
    return {
        valid: state.authReducer.validFormValues,
        snackbarOpen: state.authReducer.snackbarOpen,
        userAccount: state.adminReducer.userAccount,
        userProfile: state.authReducer.userProfile,
        errors: state.utilsReducer.errors ? state.utilsReducer.errors : {},
        confirmDialog: state.utilsReducer.confirmDialog,
        licenses: state.adminReducer.licenses,
        exhibitAs: state.adminReducer.exhibitAs,
        broker: state.adminReducer.broker,
        searchResults: state.searchReducer.autocompleteResults,
        filterQuery: state.adminReducer.filterQuery,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        handleInputChange: (field, value) => {
            dispatch(operations.handleInputChange(field, value));
        },
        handleAddArrayElement: (arrayPath) => {
            dispatch(operations.addArrayElement(arrayPath));
        },
        handleDeleteArrayElement: (arrayPath, index) => {
            dispatch(operations.deleteArrayElement(arrayPath, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleInputChange(arrayPath + "[" + index + "]." + field, value));
        },
        handleChangeAndValidate: (event) => {
            dispatch(operations.handleChangeAndValidate(event.target.id, event.target.value));
        },
        hideSnackbar: () => {
            dispatch(operations.hideSnackbar());
        },
        getUserAccount: (userAccountKey) => {
            dispatch(operations.getUserAccount(userAccountKey));
        },
        handleSaveAccountUpdates: () => {
            dispatch(operations.handleSaveAccountUpdates());
        },
        handleSearchUsers: (term) => {
            dispatch(searchOperations.searchUsers(term));
        },
        resetUserAccountState: () => {
            dispatch(operations.handleInputChange("userAccount", {}));
            dispatch(operations.handleInputChange("broker", {}));
            dispatch(operations.handleInputChange("licenses", {}));
            dispatch(operations.handleInputChange("exhibitAs", {}));
        },
        toggleTerminationDoc: (attached) => {
            dispatch(dealOperations.terminationDocUpload(attached));
        },
        handleDeleteProxy: (proxy, index) => {
            return dispatch(operations.deleteProxy(proxy, index));
        },
        handleAddProxy: (proxy) => {
            dispatch(operations.addProxy(proxy));
        },
        handlePrependArrayElement: (arrayPath) => {
            dispatch(operations.prependArrayElement(arrayPath));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminContainer);
